/**
 * dissolve.js — the holographic collapse a fighter dies in.
 *
 * A death used to be nine drifting sparkles over an empty patch of sky: the
 * body was hidden on the frame it died and the particles played to nobody in
 * particular. The problem was that nothing about it read as *this* rider being
 * destroyed — the sparkles were the same wherever and whoever they came from.
 *
 * So the effect is built out of the rider's own art instead of out of new
 * geometry. The sprite is sliced into horizontal bands and those bands are
 * sheared, torn, split into colour ghosts and dropped one at a time, which
 * looks like a projection losing its signal because that is mechanically what
 * is happening to it.
 *
 * Two constraints shape the whole module:
 *
 * 1. It is a pure function of `progress`, and progress is derived from
 *    `player.vaporizing`, which already crosses the wire. So the death costs
 *    nothing in the snapshot and the online guest sees the same collapse the
 *    host does. The particle version had to ship every particle to get that.
 *
 * 2. There is no Math.random() anywhere below. Randomness comes from a hash of
 *    (seed, band index), so a band's jitter is stable frame to frame instead of
 *    reshuffling at 60Hz — and both sides of an online match agree on it. Real
 *    randomness here reads as static, not as glitching.
 */

/** Height of one slice of the body, in source pixels. */
const BAND_H = 3;

/** Peak horizontal jitter of a single band, in pixels. */
const MAX_SHEAR = 9;

/** Displacement of a torn-away run of bands, in pixels. */
const TEAR_SHIFT = 16;

/**
 * How many discrete moments the collapse is chopped into for tearing. The tear
 * has to hold still for several frames to register as a tear — recomputed every
 * frame it just looks like noise.
 */
const TEAR_SLICES = 14;

/**
 * When bands start winking out, and over how much of the remaining life they
 * all go. Ends just short of 1 so the figure is fully gone before the respawn
 * rather than popping out on the last frame.
 */
const DROP_START = 0.18;
const DROP_SPAN = 0.78;

/** Fraction of the death spent at full opacity before the overall fade begins. */
const FADE_AFTER = 0.75;

/** Colour of the "signal" ghost. The team colour supplies the other one. */
export const HOLO_CYAN = '#00ffff';

/**
 * Deterministic hash to the unit interval.
 *
 * @param {number} a First mixed integer (conventionally a per-player seed).
 * @param {number} b Second mixed integer (conventionally a band index).
 * @returns {number} a stable value in [0, 1).
 */
function hash01(a, b) {
  let h = (Math.imul(a, 374761393) + Math.imul(b, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) | 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Natural size of an image or an offscreen canvas. */
function sizeOf(image) {
  return {
    w: image.naturalWidth || image.width,
    h: image.naturalHeight || image.height,
  };
}

/**
 * Overall opacity of the collapsing figure.
 *
 * Held at full for most of the death: fading from the first frame would make
 * the glitch detail — which is the whole effect — invisible by the time it is
 * most violent.
 *
 * @param {number} p Progress through the death, 0 → 1.
 * @returns {number} multiplier in [0, 1].
 */
export function holoAlpha(p) {
  return p < FADE_AFTER ? 1 : Math.max(0, (1 - p) / (1 - FADE_AFTER));
}

/**
 * How far the colour ghosts separate from the body. Opens up as the collapse
 * proceeds, so the figure visibly comes apart into its channels.
 *
 * @param {number} p Progress through the death, 0 → 1.
 * @returns {number} half-separation in pixels.
 */
export function chromaSplit(p) {
  return 1.5 + 7 * p;
}

/**
 * The run of bands currently torn out of alignment, if any.
 *
 * Only about half the slices tear. A tear on every slice is a continuous
 * wobble; leaving gaps is what makes the ones that do land read as failures.
 *
 * @param {number} p Progress through the death, 0 → 1.
 * @param {number} seed Per-player seed.
 * @param {number} bands Total band count.
 * @returns {{from: number, to: number, shift: number}} band range and offset.
 */
function tearAt(p, seed, bands) {
  const slice = Math.floor(p * TEAR_SLICES);
  if (hash01(seed ^ 0x5bd1, slice) < 0.5) return { from: 0, to: 0, shift: 0 };

  const from = Math.floor(hash01(seed ^ 0x9e37, slice) * bands);
  const depth = 1 + Math.floor(hash01(seed ^ 0x2545, slice) * 5);
  // Direction alternates with the slice so consecutive tears do not all drag
  // the body the same way, which would read as the figure sliding off screen.
  const dir = hash01(seed ^ 0x1b3f, slice) < 0.5 ? -1 : 1;
  return { from, to: from + depth, shift: dir * TEAR_SHIFT * (0.4 + 0.6 * p) };
}

/**
 * Paint one image as a stack of sheared, thinning horizontal bands.
 *
 * Drop-in replacement for a `ctx.drawImage(image, dx, dy)` call: it draws into
 * whatever transform the caller has already set up, which is what lets the
 * renderer route the rider, the deck and the rim silhouettes through the same
 * compositing path it always uses.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLImageElement|HTMLCanvasElement} image
 * @param {number} dx Destination x, as passed to drawImage.
 * @param {number} dy Destination y, as passed to drawImage.
 * @param {number} p Progress through the death, 0 → 1.
 * @param {number} seed Per-player seed, so the two riders never glitch in sync.
 */
export function drawHoloBands(ctx, image, dx, dy, p, seed) {
  const { w, h } = sizeOf(image);
  if (!w || !h) return;

  const bands = Math.ceil(h / BAND_H);
  const tear = tearAt(p, seed, bands);

  for (let i = 0; i < bands; i++) {
    // Each band has its own moment of failure, so the body erodes rather than
    // fading uniformly. Spread across the whole life, not just the end.
    if (p > DROP_START + hash01(seed, i) * DROP_SPAN) continue;

    const sy = i * BAND_H;
    const sh = Math.min(BAND_H, h - sy);

    // Jitter grows with the collapse: a stable figure at the moment of death,
    // shaking itself apart by the end.
    let shift = (hash01(seed, i + 977) - 0.5) * MAX_SHEAR * p;
    if (i >= tear.from && i < tear.to) shift += tear.shift;

    ctx.drawImage(image, 0, sy, w, sh, dx + shift, dy + sy, w, sh);
  }
}

/**
 * Draw the bright readout line sweeping up the collapsing body.
 *
 * Drawn from a solid-colour copy of the sprite rather than as a plain
 * rectangle, so the line is clipped to the silhouette and reads as light
 * travelling through the figure instead of a bar laid over it.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {HTMLCanvasElement} tintedImage Flat-coloured copy of the sprite.
 * @param {number} dx Destination x, as passed to drawImage.
 * @param {number} dy Destination y, as passed to drawImage.
 * @param {number} p Progress through the death, 0 → 1.
 */
export function drawHoloScan(ctx, tintedImage, dx, dy, p) {
  const { w, h } = sizeOf(tintedImage);
  if (!w || !h) return;

  // Two passes bottom to top over the life of the death.
  const travel = (p * 2) % 1;
  const scanH = Math.max(2, Math.round(h * 0.09));
  const sy = Math.round((1 - travel) * (h - scanH));

  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha *= 0.85 * holoAlpha(p);
  ctx.drawImage(tintedImage, 0, sy, w, scanH, dx, dy + sy, w, scanH);
  ctx.restore();
}
